import {
  PencilSquareIcon,
  IdentificationIcon,
  FunnelIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/solid";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  RecoilState,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import ButtonForModal from "../../../../components/ButtonForModal";
import { useHistory } from "react-router-dom";
import { notificationFilterById } from "../../../../actions/Utilities/NotificationFilter";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { toast } from "../../../../components/toastNotification";
import {
  busLicensePermitFromOffice,
  busLicensesAddState,
  busLicensesEditState,
  busLicensesShowState,
  rowForEditBusLicense,
  selectedRowsStateBLP,
  busLPViewData,
  businessLicenseFromOffice,
  businessLicenseOfficeId,
  disableBLOfficeField,
} from "../atoms/atoms";
import { BusLicensesAndPermitsForm } from "../forms/BusLicensesAndPermitsForm";
import { BusLicensesAndPermitsRowDetails } from "./BuslicenseAndPermitRowDetails";
import {
  BLPTableRowDto,
  BLPSortDirection,
  BLPSortField,
  getBLPTable,
} from "../api/getBLPTable";
import { FilteredBLPFilters, getFilteredBLP } from "../api/getFilteredBLP";
import { getBLPbyId } from "../api/getBLPbyId";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import { useDeactivateBLP } from "../api/deactivateBLP";
import { mainSelectedTab, paginationData } from "../../atoms";
import { useGetBusLicensePermitForEdit } from "../hooks/useGetBusLicensePermitForEdit";
import MaterialTableTS from "../../../../components/tables/MaterialTableTS";
import { OfficeTabs } from "../../../OfficeTab/OfficeTabs";
import { BusLicensesAndPermitsTableFilter } from "../filters/TableFilter";
import type { DownloadBLPExcelRequest } from "../api/downloadBLPExcel";

export interface BLPNotificationRouteState {
  recordId?: string | number | null;
  moduleName?: string | null;
  subModuleName?: string | null;
  action?: string | null;
  blpData?: any;
}

const TABLE_PAGE_SIZE = 10;

const getOldLicenseLabel = (
  isOldLicense: boolean | undefined | null,
): string => {
  if (isOldLicense === true) {
    return "TRUE";
  }

  if (isOldLicense === false) {
    return "FALSE";
  }

  return "";
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);

  return Number.isNaN(numericValue) ? null : numericValue;
};

const getBLPSortValue = (
  row: BLPTableRowDto,
  field: string,
): string | number | null => {
  switch (field) {
    case "recordId":
      return Number(row.recordId);

    case "storeNumber":
      return row.storeNumber ?? null;

    case "busLicensePermitTypeName":
      return row.busLicensePermitTypeName ?? "";

    case "issuerName":
      return row.issuerName ?? "";

    case "licenseOrPermitNumber":
      return row.licenseOrPermitNumber ?? "";

    case "issuedDate":
      return row.issuedDate ? new Date(row.issuedDate).getTime() : null;

    case "renewedDate":
      return row.renewedDate ? new Date(row.renewedDate).getTime() : null;

    case "expiration_Date":
      return row.expiration_Date
        ? new Date(row.expiration_Date).getTime()
        : null;

    case "licenseStatus":
      return row.licenseStatus ?? "";

    case "isOldLicense":
      if (row.isOldLicense === true) {
        return 1;
      }

      if (row.isOldLicense === false) {
        return 0;
      }

      return null;

    default:
      return null;
  }
};

const sortBLPRows = (
  rows: BLPTableRowDto[],
  sortField?: string,
  sortDirection: BLPSortDirection = "asc",
): BLPTableRowDto[] => {
  if (!sortField) {
    return rows;
  }

  return [...rows].sort((firstRow, secondRow) => {
    const firstValue = getBLPSortValue(firstRow, sortField);

    const secondValue = getBLPSortValue(secondRow, sortField);

    if (firstValue === null && secondValue === null) {
      return 0;
    }

    if (firstValue === null) {
      return 1;
    }

    if (secondValue === null) {
      return -1;
    }

    let comparison = 0;

    if (typeof firstValue === "number" && typeof secondValue === "number") {
      comparison = firstValue - secondValue;
    } else {
      comparison = String(firstValue).localeCompare(
        String(secondValue),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        },
      );
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });
};

const matchesBLPSearch = (row: BLPTableRowDto, search: string): boolean => {
  const normalizedSearch = search.trim().toLowerCase();

  if (!normalizedSearch) {
    return true;
  }

  const recordNumber = "LP-" + String(row.recordId).padStart(4, "0");

  const issuedDate = row.issuedDate
    ? new Date(row.issuedDate).toLocaleDateString()
    : "";

  const renewedDate = row.renewedDate
    ? new Date(row.renewedDate).toLocaleDateString()
    : "";

  const searchableValues = [
    row.recordId,
    recordNumber,
    row.storeNumber,
    row.busLicensePermitTypeName,
    row.issuerName,
    row.licenseOrPermitNumber,
    issuedDate,
    renewedDate,
    row.expiration_Date,
    row.licenseStatus,
    getOldLicenseLabel(row.isOldLicense),
  ];

  return searchableValues.some((value) =>
    String(value ?? "")
      .toLowerCase()
      .includes(normalizedSearch),
  );
};

const findTabIndex = (userPermissions: string[], url: string): number => {
  let userTabs = [{ to: "" }];

  [...OfficeTabs].forEach((tabs) => {
    const hasPermission = userPermissions.includes(tabs.permission);

    if (hasPermission) {
      userTabs.push({
        to: tabs.to,
      });
    }
  });

  userTabs.shift();

  return userTabs.findIndex((tab) => tab.to === url);
};

const shouldSyncPage = (
  prevPage: string | null,
  isAdd: boolean,
  isEdit: boolean,
  isShow: boolean,
  showFromOffice: boolean,
): boolean =>
  prevPage !== null && (isAdd || isEdit || isShow || showFromOffice);

const DeleteConfirmMessage: React.FC<{
  selectedRowsToDelete: any[] | undefined;
}> = ({ selectedRowsToDelete }) => {
  const count = selectedRowsToDelete?.length ?? 0;

  const label =
    count > 1 ? "Business Licenses and Permits" : "Business License and Permit";

  return (
    <div className="text-gray-500 font-normal">
      {`Are you sure you want to deactivate ${count} ${label}?`}
    </div>
  );
};

const ActionCell: React.FC<{
  canEdit: boolean;
  onEdit: () => void;
}> = ({ canEdit, onEdit }) => {
  if (!canEdit) {
    return null;
  }

  return (
    <div className="place content-center ml-4">
      <ButtonForModal
        className={"border border-quaternary text-white bg-quaternary"}
        onClick={onEdit}
        isDisabled={undefined}
      >
        <PencilSquareIcon className="h-4 w-4" />
        Edit
      </ButtonForModal>
    </div>
  );
};

interface BusLicensesAndPermitsTableProps {
  officeId?: string | number;
  viewFromOffice?: boolean;
  onBackToDashboard?: () => void;
  handleDownload?: (request?: DownloadBLPExcelRequest) => void | Promise<void>;
  downloading?: boolean;
  initialStatus?: string | null;
  initialRecordId?: string | null;
  notificationRouteState?: BLPNotificationRouteState | null;
  onCloseRecordDetails?: () => void;
}

export const BusLicensesAndPermitsTable: React.FC<
  BusLicensesAndPermitsTableProps
> = ({
  officeId,
  viewFromOffice,
  onBackToDashboard,
  handleDownload,
  downloading,
  initialStatus,
  initialRecordId,
  notificationRouteState,
  onCloseRecordDetails,
}) => {
  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);

  const [viewBusinessFromOffice, setViewFromOffice] = useRecoilState(
    businessLicenseFromOffice,
  );

  const [showFromOffice] = useRecoilState(busLicensePermitFromOffice);

  const [, setDisableOfficeField] = useRecoilState(disableBLOfficeField);

  const [isAdd, setIsAddState] = useRecoilState(busLicensesAddState);

  const setRowToEdit = useSetRecoilState(rowForEditBusLicense);

  const [isEdit, setIsEditState] = useRecoilState(busLicensesEditState);

  const [isShow, setShowState] = useRecoilState(busLicensesShowState);

  const [filteredBusLicensePermit, setFilteredBusLicensePermit] = useState<
    BLPTableRowDto[]
  >([]);

  const notificationRecordId = notificationRouteState?.recordId;

  const hasNotificationRecord =
    notificationRecordId !== null &&
    notificationRecordId !== undefined &&
    notificationRecordId !== "";

  const [notificationRows, setNotificationRows] = useState<
    BLPTableRowDto[] | null
  >(null);

  const [notificationMode, setNotificationMode] = useState<boolean>(
    hasNotificationRecord,
  );

  const [isLoadingNotificationRows, setIsLoadingNotificationRows] =
    useState<boolean>(hasNotificationRecord);

  const [isLoadingInitialRecord, setIsLoadingInitialRecord] = useState<boolean>(
    Boolean(initialRecordId),
  );

  const [isSearchingFilteredBLP, setIsSearchingFilteredBLP] = useState<boolean>(
    !hasNotificationRecord,
  );

  const [activeFilters, setActiveFilters] = useState<FilteredBLPFilters | null>(
    null,
  );

  const [tableQueryVersion, setTableQueryVersion] = useState(0);

  const [tablePageSize, setTablePageSize] = useState(TABLE_PAGE_SIZE);

  const [page, setPage] = useRecoilState(paginationData);

  const [rowViewData, setRowViewData] = useRecoilState(
    busLPViewData as unknown as RecoilState<any>,
  );

  const [busLicensePermitData, setBusLicensePermitData] = useState<any[]>([]);

  const [currentSearch, setCurrentSearch] = useState("");

  const userPermissions = useRecoilValue(permissionsSelector);

  const addPermission = PERMISSIONS.permission_buslicensesandpermits_add;

  const editPermission = PERMISSIONS.permission_buslicensesandpermits_edit;

  const deletePermission = PERMISSIONS.permission_buslicensesandpermits_delete;

  const history = useHistory();

  const [tableFilter, setTableFilter] = useState(false);

  const [blOfficeId] = useRecoilState(businessLicenseOfficeId);

  const [openModalDelete, setOpenModalDelete] = useState(false);

  const { mutateAsync: deactivateBLP, isLoading: isDeactivating } =
    useDeactivateBLP();

  const deactivateLockRef = useRef(false);

  const [selectedRowsToDelete, setSelectedRowsToDelete] =
    useRecoilState(selectedRowsStateBLP);

  const { mutate: getBLPForEdit } = useGetBusLicensePermitForEdit();

  const [dashboardFilter, setDashboardFilter] = useState<string>(
    initialStatus?.trim() || "TOTAL",
  );

  useEffect(() => {
    setDashboardFilter(initialStatus?.trim() || "TOTAL");
    setIsSearchingFilteredBLP(true);
  }, [initialStatus]);

  const resolvedOfficeId = officeId ?? blOfficeId;

  const normalizedOfficeId =
    resolvedOfficeId !== null &&
    resolvedOfficeId !== undefined &&
    resolvedOfficeId !== ""
      ? Number(resolvedOfficeId)
      : null;

  const loadBLPTablePage = useCallback(
    async (query: any) => {
      const currentPage = Number(query.page ?? 0);

      const pageNumber = currentPage + 1;

      const pageSize =
        Number(query.pageSize) > 0 ? Number(query.pageSize) : tablePageSize;

      const sortField =
        typeof query.orderBy?.field === "string"
          ? (query.orderBy.field as BLPSortField)
          : "recordId";

      const sortDirection: BLPSortDirection =
        query.orderDirection === "asc" ? "asc" : "desc";

      const search = String(query.search ?? "").trim();

      setCurrentSearch(search);

      if (notificationMode) {
        setIsSearchingFilteredBLP(false);
        const allNotificationRows = notificationRows ?? [];

        const searchedNotificationRows =
          search === ""
            ? allNotificationRows
            : allNotificationRows.filter((row) =>
                matchesBLPSearch(row, search),
              );

        const sortedNotificationRows = sortBLPRows(
          searchedNotificationRows,
          sortField,
          sortDirection,
        );

        const startIndex = currentPage * pageSize;

        const pageRows = sortedNotificationRows.slice(
          startIndex,
          startIndex + pageSize,
        );

        return {
          data: pageRows,
          page: currentPage,
          totalCount: sortedNotificationRows.length,
        };
      }

      setIsSearchingFilteredBLP(true);

      try {
        const result =
          activeFilters === null
            ? await getBLPTable({
                dashboardFilter,
                officeId: normalizedOfficeId,

                pageNumber,
                pageSize,
                search,
                sortField,
                sortDirection,
              })
            : await getFilteredBLP({
                ...activeFilters,

                pageNumber,
                pageSize,
                search,
                sortField,
                sortDirection,
              });
        const rows = (result.items ?? []).map((item) => ({
          ...item,

          isActive:
            String(item.licenseStatus ?? "")
              .trim()
              .toLowerCase() !== "inactive",
        }));
        if (activeFilters === null) {
          setBusLicensePermitData(rows);
        } else {
          setFilteredBusLicensePermit(rows);
        }

        return {
          data: rows,

          page: Math.max(0, Number(result.pageNumber ?? pageNumber) - 1),

          totalCount: Number(result.totalCount ?? 0),
        };
      } catch {
        toast.fire(
          "Server Error",
          "Cannot retrieve Business License and Permit records.",
          "error",
        );

        return {
          data: [],
          page: 0,
          totalCount: 0,
        };
      } finally {
        setIsSearchingFilteredBLP(false);
      }
    },
    [
      activeFilters,
      dashboardFilter,
      normalizedOfficeId,
      notificationMode,
      notificationRows,
      tablePageSize,
    ],
  );

  const remoteTableKey = [
    dashboardFilter,
    normalizedOfficeId ?? "all-offices",
    JSON.stringify(activeFilters ?? {}),
    notificationMode
      ? `notification-${String(notificationRecordId)}`
      : "normal-table",
    tableQueryVersion,
  ].join("|");

  const alignCenter = "center" as
    | "center"
    | "inherit"
    | "justify"
    | "left"
    | "right"
    | undefined;

  useEffect(() => {
    if (!initialRecordId) {
      setIsLoadingInitialRecord(false);
      return;
    }

    let isMounted = true;

    const loadInitialRecord = async () => {
      setIsLoadingInitialRecord(true);

      try {
        const record = await getBLPbyId(initialRecordId);

        if (!isMounted) {
          return;
        }

        setRowViewData(record);
        setShowState(true);

        localStorage.setItem("Data", JSON.stringify(record));
      } catch {
        if (!isMounted) {
          return;
        }

        toast.fire(
          "Server Error",
          "Cannot retrieve Business License and Permit details.",
          "error",
        );
      } finally {
        if (isMounted) {
          setIsLoadingInitialRecord(false);
        }
      }
    };

    loadInitialRecord();

    return () => {
      isMounted = false;
    };
  }, [initialRecordId, setRowViewData, setShowState]);

  const handleLink = (moduleName: string) => {
    const url = `/officesettings/${moduleName}`;

    history.push(moduleName);

    const index = findTabIndex(userPermissions, url);

    setSelectedIndex(index);
  };

  const handleSelectedRowsChange = (rows: any[]) => {
    const forDelete = (rows ?? [])
      .filter((row) => row.isActive)
      .map((row) => {
        const { tableData, ...rest } = row;

        return rest;
      });

    setSelectedRowsToDelete(forDelete);
  };

  const handleDelete = async (): Promise<void> => {
    if (deactivateLockRef.current || isDeactivating) {
      return;
    }

    const recordIds = (selectedRowsToDelete ?? [])
      .map((row) => Number(row.recordId))
      .filter((recordId) => Number.isInteger(recordId) && recordId > 0);

    if (recordIds.length === 0) {
      toast.fire(
        "Deactivation Failed",
        "No valid records were selected.",
        "warning",
      );

      return;
    }

    deactivateLockRef.current = true;

    try {
      await deactivateBLP(recordIds);

      setSelectedRowsToDelete([]);

      setOpenModalDelete(false);

      if (
        tableRef.current &&
        typeof tableRef.current.onQueryChange === "function"
      ) {
        tableRef.current.onQueryChange();
      }
    } finally {
      deactivateLockRef.current = false;
    }
  };

  const onClickBusLP = async (rowDetails: BLPTableRowDto) => {
    try {
      const fullRecord = await getBLPbyId(rowDetails.recordId);

      if (viewBusinessFromOffice) {
        setViewFromOffice(true);

        handleLink("buslicensesandpermits");
      }

      setRowViewData(fullRecord);

      setShowState(true);

      localStorage.setItem("Data", JSON.stringify(fullRecord));
    } catch {
      toast.fire(
        "Server Error",
        "Cannot retrieve Business License and Permit details.",
        "error",
      );
    }
  };

  const onClickCancelShow = () => {
    if (viewBusinessFromOffice) {
      setViewFromOffice(false);

      handleLink("officesetting");
    }

    setDisableOfficeField(false);

    setShowState(false);
    onCloseRecordDetails?.();
    setBusLicensePermitData(
      busLicensePermitData.map((data) => ({
        ...data,
      })),
    );

    if (filteredBusLicensePermit?.length > 0) {
      setFilteredBusLicensePermit(
        filteredBusLicensePermit.map((data) => ({
          ...data,
        })),
      );
    }

    localStorage.removeItem("Data");
  };

  const handleEdit = (rowDetails) => {
    getBLPForEdit(rowDetails.recordId, {
      onSuccess: (res) => {
        const editRecord = res;
        const licenseStatusId =
          editRecord.licenseStatusId ??
          (typeof editRecord.licenseStatus === "number" ||
          /^\d+$/.test(String(editRecord.licenseStatus ?? ""))
            ? editRecord.licenseStatus
            : null);

        const renewalStatusId =
          editRecord.renewalStatusId ??
          (typeof editRecord.renewalStatus === "number" ||
          /^\d+$/.test(String(editRecord.renewalStatus ?? ""))
            ? editRecord.renewalStatus
            : null);

        setRowToEdit({
          ...res,

          recordName:
            rowDetails.recordId === null || rowDetails.recordId === undefined
              ? null
              : "LP-" + String(rowDetails.recordId).padStart(4, "0"),

          createdDate: res.createdDate
            ? String(res.createdDate).slice(0, 10)
            : null,

          expirationDate: res.expirationDate
            ? String(res.expirationDate).slice(0, 10)
            : null,

          issuedDate: res.issuedDate
            ? String(res.issuedDate).slice(0, 10)
            : null,

          renewedDate: res.renewedDate
            ? String(res.renewedDate).slice(0, 10)
            : null,

          licenseStatus: toNullableNumber(licenseStatusId),

          renewalStatus: toNullableNumber(renewalStatusId),

          isOldLicense: res.isOldLicense ?? false,

          isLicenseRequired: res.isLicenseRequired ?? false,
        });

        setIsEditState(true);
      },
    });
  };

  useEffect(() => {
    setSelectedRowsToDelete([]);
  }, [setSelectedRowsToDelete]);

  useEffect(() => {
    if (notificationRouteState?.action !== "VendorCancelToBlp") {
      return;
    }

    if (!notificationRouteState.blpData) {
      return;
    }

    setRowViewData(notificationRouteState.blpData);

    setShowState(true);
  }, [
    notificationRouteState?.action,
    notificationRouteState?.blpData,
    setRowViewData,
    setShowState,
  ]);

  useEffect(() => {
    if (!hasNotificationRecord) {
      setNotificationMode(false);
        setNotificationRows(null);

      setIsLoadingNotificationRows(false);

        localStorage.removeItem("NotifFilter");

        localStorage.removeItem("ModuleFilter");

      return;
    }

    let isMounted = true;

    const loadNotificationRows = async () => {
      setNotificationMode(true);
      setNotificationRows(null);
      setIsLoadingNotificationRows(true);

      setActiveFilters(null);
      setDashboardFilter("TOTAL");
      setSelectedRowsToDelete([]);
      setPage(0);

      try {
        let result: any[] = [];

        if (notificationRouteState?.action === "RecentActivityToBlp") {
          const recentRecordIds = String(notificationRecordId)
            .split(".")
            .map((recordId) => recordId.trim())
            .filter((recordId) => recordId !== "");

          const recentRecords = await Promise.all(
            recentRecordIds.map((recordId) => getBLPbyId(recordId)),
          );

          result = recentRecords.flatMap((response: any) => {
            if (!response) {
              return [];
            }

            if (Array.isArray(response)) {
              return response;
            }

            if (Array.isArray(response.data)) {
              return response.data;
            }

            if (response.data && typeof response.data === "object") {
              return [response.data];
            }

            return [response];
          });
        } else {
          const notificationResult = await notificationFilterById(
            String(notificationRecordId),
            notificationRouteState?.moduleName ?? undefined,
            notificationRouteState?.subModuleName ?? undefined,
          );

          result = Array.isArray(notificationResult) ? notificationResult : [];
        }

        if (!isMounted) {
          return;
        }

        const rows = result.map((item) => ({
          ...item,

          expiration_Date:
            item.expiration_Date ??
            (item.expirationDate
              ? new Date(item.expirationDate).toLocaleDateString()
              : ""),

          isActive:
            String(item.licenseStatus ?? "")
              .trim()
              .toLowerCase() !== "inactive",
        }));

        setNotificationRows(rows);

        setFilteredBusLicensePermit(rows);

        setTableQueryVersion((current) => current + 1);
      } catch {
        if (!isMounted) {
          return;
        }

        setNotificationRows([]);

        setFilteredBusLicensePermit([]);

        toast.fire(
          "Server Error",
          "Cannot retrieve the records from the recent activity.",
          "error",
        );
      } finally {
        if (isMounted) {
          setIsLoadingNotificationRows(false);
        }
      }
    };

    loadNotificationRows();

    return () => {
      isMounted = false;
    };
  }, [
    hasNotificationRecord,
    notificationRecordId,
    notificationRouteState?.action,
    notificationRouteState?.moduleName,
    notificationRouteState?.subModuleName,
    setPage,
    setSelectedRowsToDelete,
  ]);

  const handleFilterSearch = async (
    filters: FilteredBLPFilters,
  ): Promise<void> => {
    setIsSearchingFilteredBLP(true);
    setNotificationMode(false);
    setNotificationRows(null);

    setSelectedRowsToDelete([]);

    setPage(0);

    setActiveFilters({
      busLicensePermitTypeId: [...filters.busLicensePermitTypeId],

      issuerId: [...filters.issuerId],

      licenseOrPermitNumberId: [...filters.licenseOrPermitNumberId],

      storeNumberId: [...filters.storeNumberId],

      licenseStatusId: filters.licenseStatusId,

      isOldLicense: filters.isOldLicense,
    });

    setTableQueryVersion((current) => current + 1);
  };

  const handleFilterRefresh = () => {
    setIsSearchingFilteredBLP(true);
    setNotificationMode(false);
    setNotificationRows(null);

    setDashboardFilter("TOTAL");

    setActiveFilters(null);

    setFilteredBusLicensePermit([]);

    setSelectedRowsToDelete([]);

    setPage(0);

    setTableQueryVersion((current) => current + 1);
  };

  const matColumns = [
    {
      title: "Record Number",
      width: "5%",
      field: "recordId",

      render: (rowData: BLPTableRowDto) => (
        <button
          type="button"
          className="cursor-pointer border-0 bg-transparent p-0 text-left"
          onClick={() => onClickBusLP(rowData)}
        >
          <span className="blueAndBold underline">
            {"LP-" + String(rowData.recordId).padStart(4, "0")}
          </span>

          <br />
        </button>
      ),

      customFilterAndSearch: (term: string, rowData: BLPTableRowDto) =>
        ("LP-" + String(rowData.recordId).padStart(4, "0"))
          .toLowerCase()
          .indexOf(term.toLowerCase()) !== -1,
    },
    {
      title: "Store Number",
      field: "storeNumber",
    },
    {
      title: "Type",
      field: "busLicensePermitTypeName",
    },
    {
      title: "Issuer",
      field: "issuerName",
    },
    {
      title: "License/Permit Number",
      field: "licenseOrPermitNumber",
    },
    {
      title: "Issued Date",
      field: "issuedDate",

      render: (rowData: BLPTableRowDto) => (
        <div>
          {rowData.issuedDate
            ? new Date(rowData.issuedDate).toLocaleDateString()
            : ""}
        </div>
      ),
    },
    {
      title: "Renewed Date",
      field: "renewedDate",

      render: (rowData: BLPTableRowDto) => (
        <div>
          {rowData.renewedDate
            ? new Date(rowData.renewedDate).toLocaleDateString()
            : ""}
        </div>
      ),
    },
    {
      title: "Expiration Date",
      field: "expiration_Date",

      render: (rowData: BLPTableRowDto) => <div>{rowData.expiration_Date}</div>,
    },
    {
      title: "License Status",
      field: "licenseStatus",

      render: (rowData: BLPTableRowDto) => (
        <div className="font-sans uppercase mr-4 text-xs">
          {rowData.licenseStatus}
        </div>
      ),
    },
    {
      title: "Old License",
      field: "isOldLicense",

      render: (rowData: BLPTableRowDto) => (
        <div className="font-sans uppercase mr-4 text-xs">
          {getOldLicenseLabel(rowData.isOldLicense)}
        </div>
      ),
    },
    {
      title: "Action",
      align: alignCenter,
      name: "Action/s",
      width: "15%",
      sorting: false,

      render: (rowData: BLPTableRowDto) => (
        <ActionCell
          canEdit={userPermissions.includes(editPermission)}
          onEdit={() => handleEdit(rowData)}
        />
      ),
    },
  ];

  const tableRef = useRef<any>(null);

  const handleDownloadClick = () => {
    if (!handleDownload) {
      return;
    }

    if (activeFilters !== null) {
      handleDownload({
        busLicensePermitTypeId: activeFilters.busLicensePermitTypeId,

        issuerId: activeFilters.issuerId,

        licenseOrPermitNumberId: activeFilters.licenseOrPermitNumberId,

        storeNumberId: activeFilters.storeNumberId,

        licenseStatusId: activeFilters.licenseStatusId,

        isOldLicense: activeFilters.isOldLicense,

        search: currentSearch || null,
      });

      return;
    }

    handleDownload({
      dashboardFilter,
      officeId: normalizedOfficeId,
      search: currentSearch || null,
    });
  };

  const prevPage = new URLSearchParams(globalThis.window.location.search).get(
    "prevPage",
  );

  useEffect(() => {
    if (prevPage === null) {
      return;
    }

    const previousPageNumber = Number(prevPage);

    if (Number.isNaN(previousPageNumber)) {
      return;
    }

    const shouldRestorePage = shouldSyncPage(
      prevPage,
      isAdd,
      isEdit,
      isShow,
      showFromOffice,
    );

    if (shouldRestorePage && page !== previousPageNumber) {
      setPage(previousPageNumber);
    }
  }, [prevPage, isAdd, isEdit, isShow, showFromOffice, page, setPage]);

  let mainContent: React.ReactNode;

  if (isAdd || isEdit) {
    mainContent = (
      <div className={``}>
        <BusLicensesAndPermitsForm
          isAdd={isAdd}
          isEdit={isEdit}
          setIsAdd={setIsAddState}
          setIsEdit={setIsEditState}
        />
      </div>
    );
  } else if (isShow || viewBusinessFromOffice) {
    mainContent = (
      <div className="relative py-2 box-shadow border-quinary border-l-2">
        <div className="">
          <BusLicensesAndPermitsRowDetails
            rowData={rowViewData}
            rowIsExpanded={true}
            handleEdit={handleEdit}
            onClickCancelShow={onClickCancelShow}
            setIsAdd={setIsAddState}
            setReset={false}
          />
        </div>
      </div>
    );
  } else {
    mainContent = (
      <div className="">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex gap-2 items-center">
              <div>
                <IdentificationIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
              </div>

              <div className="flex flex-col leading-tight">
                <h3 className="text-xs font-medium text-gray-500">
                  Business Licenses &amp; Permits Module
                </h3>

                <h1 className="mr-auto font-bold text-primary text-md uppercase">
                  ALL Business Licenses &amp; Permits
                </h1>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-row justify-between py-2">
          <div className="text-secondary italic text-xs3">
            {userPermissions.includes(deletePermission) && (
              <div className="pb-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>

                <span>Tick the box to deactivate some data.</span>
              </div>
            )}

            <div className="space-x-4 mr-6">
              <button
                className="hover:scale-90 transition duration-500 flex justify-center items-center shadow-md px-3 py-1 h-7 text-xs
                  gap-x-2 font-semibold text-primary border hover:bg-primary selected hover:text-white border-primary rounded-md"
                onClick={() => setTableFilter((current) => !current)}
              >
                <div>Filter By</div>

                <FunnelIcon className="h-3 w-3 focus:bg-primary" />
              </button>
            </div>
          </div>

          <div className="gap-2 flex items-center">
            {userPermissions.includes(addPermission) && (
              <button
                type="button"
                onClick={() => setIsAddState(true)}
                className="inline-flex group items-center gap-2 hover:scale-90 transition duration-500 shadow-md px-3 py-1 h-7 text-xs font-semibold text-[#e91e8c] border border-[#e91e8c] hover:bg-[#e91e8c] hover:text-white rounded-full"
              >
                <span className="inline-flex items-center justify-center rounded-full h-4 w-4 bg-[#e91e8c] group-hover:bg-white transition">
                  <span
                    className="text-white group-hover:text-[#e918ec] font-bold text-sm leading-none"
                    style={{
                      marginTop: "-1px",
                    }}
                  >
                    +
                  </span>
                </span>
                <span>Add</span>
              </button>
            )}

            <button
              type="button"
              onClick={handleDownloadClick}
              disabled={downloading}
              className="inline-flex group items-center gap-2 hover:scale-90 transition duration-500 shadow-md px-3 py-1 h-7 text-xs font-semibold text-[#1565c0] border border-[#1565c0] hover:bg-[#1565c0] hover:text-white rounded-full disabled:opacity-50"
            >
              <span className="inline-flex items-center justify-center rounded-full h-4 w-4 bg-[#1565c0] group-hover:bg-white transition">
                <ArrowRightIcon
                  className="h-3 w-3 text-white group-hover:text-[#1565c0] rotate-90"
                  aria-hidden="true"
                />
              </span>

              {downloading ? "Downloading..." : "Download File"}
            </button>

            {onBackToDashboard && (
              <button
                type="button"
                onClick={onBackToDashboard}
                className="inline-flex group hover:scale-90 transition duration-500 shadow-md px-3 py-1 h-7 text-xs font-semibold text-primary border hover:bg-primary hover:text-white border-primary rounded-md items-center"
              >
                <span>Back to Dashboard</span>

                <span className="ml-2 inline-flex items-center justify-center rounded-full h-4 w-4 bg-primary group-hover:bg-white transition">
                  <ArrowRightIcon
                    className="h-3 w-3 text-white group-hover:text-primary transform rotate-180"
                    aria-hidden="true"
                  />
                </span>
              </button>
            )}
          </div>
        </div>

        <main className="flex justify-between">
          {tableFilter && (
            <div className="w-1/5 shrink-0 flex-col overflow-y-auto bg-white">
              <div className="mb-4">
                {!isAdd && !isEdit && !isShow && (
                  <BusLicensesAndPermitsTableFilter
                    tableFilter={tableFilter}
                    isSearching={isSearchingFilteredBLP}
                    onSearch={handleFilterSearch}
                    onRefresh={handleFilterRefresh}
                  />
                )}
              </div>
            </div>
          )}

          <div
            className={
              tableFilter
                ? "w-4/5 mb-4 bg-white overflow-x-auto box-shadow"
                : "w-full mb-4 bg-white overflow-x-auto box-shadow"
            }
          >
            <MaterialTableTS
              key={remoteTableKey}
              columns={matColumns as any}
              data={loadBLPTablePage as any}
              isLoading={
                isLoadingNotificationRows ||
                isSearchingFilteredBLP ||
                isLoadingInitialRecord
              }
              toolbarSearch={!viewFromOffice}
              selectedRows={selectedRowsToDelete}
              selection={userPermissions.includes(deletePermission)}
              showToolbar={true}
              onSelectionChange={handleSelectedRowsChange}
              setConfirmModalOpen={setOpenModalDelete}
              tableRef={tableRef}
              paging={true}
              pageSize={tablePageSize}
              onChangeRowsPerPage={(newPageSize: number) => {
                setTablePageSize(newPageSize);
                setSelectedRowsToDelete([]);
              }}
            />

            <ConfirmBoxDelete
              ModalTitle="Deactivate Business License And Permit"
              qConfirmation={
                <DeleteConfirmMessage
                  selectedRowsToDelete={selectedRowsToDelete}
                />
              }
              isOpen={openModalDelete}
              onClose={() => setOpenModalDelete(false)}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </div>
        </main>
      </div>
    );
  }

  return <div className="px-2 py-2 min-h-fit">{mainContent}</div>;
};